import { Injectable, Signal, computed, linkedSignal } from '@angular/core';
import { isEmpty } from 'lodash';
import { SortParams } from 'mbs-ui-kit/models/sort';
import { QueryFiltersParams, QueryParams } from 'mbs-ui-shared@models';
import { Observable } from 'rxjs';
import { PagingAndDataStoreInterface } from './withPagingAndData.types';

// eslint-disable-next-line @typescript-eslint/ban-types
@Injectable({ providedIn: 'root' })
export abstract class PagingAndDataFacade<T extends object, F extends object = Record<string, never>> {
  /**
   * Subclasses must inject the concrete signal store and assign it here.
   * e.g. `protected override store = inject(AdministratorsStore);`
   */
  protected abstract store: PagingAndDataStoreInterface<T, F>;

  // ---------------------------------------------------------------------------
  // Entity selectors — Signal pass-throughs
  // ---------------------------------------------------------------------------

  entityById(id: string): Signal<T | undefined> {
    return this.store.entityById(id);
  }

  // "Loaded" means the entity has been loaded by id or by ids, so the entity details could be more complete than the one in the current page or data list (e.g. it has more fields loaded).
  loadedEntityById(id: string): Signal<T | undefined> {
    return this.store.loadedEntityById(id);
  }

  entitiesById(ids: string[]): Signal<T[]> {
    return this.store.entitiesById(ids);
  }

  // "Loaded" means the entities have been loaded by id or by ids, so the entities details could be more complete than the one in the current page or data list (e.g. it has more fields loaded).
  loadedEntitiesById(ids: string[]): Signal<T[]> {
    return this.store.loadedEntitiesById(ids);
  }

  get anyFirst(): Signal<T | undefined> {
    return this.store.selectFirstEntity;
  }

  loadingForId(id: string): Signal<boolean> {
    return this.store.loadingForId(id);
  }

  missedById(id: string): Signal<boolean> {
    return this.store.missedById(id);
  }

  missedByIds(ids: string[]): Signal<string[]> {
    return this.store.missedByIds(ids);
  }

  get loading(): Signal<boolean> {
    return this.store.selectLoading;
  }

  // ---------------------------------------------------------------------------
  // "Data" region
  // ---------------------------------------------------------------------------

  get dataLoading(): Signal<boolean> {
    return this.store.selectDataLoading;
  }

  get dataLoaded(): Signal<boolean> {
    return this.store.selectDataLoaded;
  }

  get dataTotal(): Signal<number> {
    return this.store.selectDataTotal;
  }

  get data(): Signal<T[]> {
    return this.store.selectData;
  }

  get dataCount(): Signal<number> {
    return this.store.selectDataCount;
  }

  get allDataLoaded(): Signal<boolean> {
    return this.store.selectAllDataLoaded;
  }

  get dataHasError(): Signal<boolean> {
    return this.store.selectDataHasError;
  }

  // ---------------------------------------------------------------------------
  // Paging region
  // ---------------------------------------------------------------------------

  get pagingLoading(): Signal<boolean> {
    return this.store.selectPagingLoading;
  }

  get pagingLoaded(): Signal<boolean> {
    return this.store.selectPagingLoaded;
  }

  get pagingCurrentPageNumber(): Signal<number> {
    return this.store.selectPagingCurrentPageNumber;
  }

  get pagingPerPage(): Signal<number | null> {
    return this.store.selectPagingPerPage;
  }

  get pagingTotal(): Signal<number> {
    return this.store.selectPagingTotal;
  }

  /** Derived: { perPage, currentPageNumber } */
  readonly pagingParams: Signal<{ perPage: number | null; currentPageNumber: number }> = computed(() => ({
    perPage: this.store.selectPagingPerPage(),
    currentPageNumber: this.store.selectPagingCurrentPageNumber()
  }));

  get pagingFilters(): Signal<QueryFiltersParams> {
    return this.store.selectPagingFilters;
  }

  get currentPage(): Signal<T[]> {
    return this.store.selectCurrentPage;
  }

  get pagingHasError(): Signal<boolean> {
    return this.store.selectPagingHasError;
  }

  get sortSettings(): Signal<SortParams | null> {
    return this.store.selectSortSettings;
  }

  /** Derived: true when filters are empty and page is 1 */
  readonly pagingEmptyFiltersAndFirstCurrentPage: Signal<boolean> = computed(
    () => isEmpty(this.store.selectPagingFilters()) && this.store.selectPagingCurrentPageNumber() === 1
  );

  /**
   * Current page that holds the previous value while loading to avoid table flicker.
   */
  readonly currentPageNonPurging: Signal<T[]> = linkedSignal({
    source: () => ({ page: this.store.selectCurrentPage(), loading: this.store.selectPagingLoading() }),
    computation: ({ page, loading }, previous) => (loading && previous !== undefined ? previous.value : page)
  });

  // Combined
  get dataEntityLoading(): Signal<boolean> {
    return this.store.selectDataEntityLoading;
  }

  get pagingEntityLoading(): Signal<boolean> {
    return this.store.selectPagingEntityLoading;
  }

  /** Derived: loaded && !loading */
  readonly pagingTotalLoaded: Signal<boolean> = computed(() => this.store.selectPagingLoaded() && !this.store.selectPagingLoading());

  /** Derived: data when dataLoaded is true, otherwise empty array */
  readonly dataWhenLoaded: Signal<T[]> = computed(() => (this.store.selectDataLoaded() ? this.store.selectData() : []));

  // ---------------------------------------------------------------------------
  // Commands
  // ---------------------------------------------------------------------------

  public loadById(params: { id: string; quiet?: boolean; force?: boolean }): void {
    this.store.loadEntityById(params);
  }

  public loadByIds(params: { ids: string[]; quiet?: boolean; force?: boolean }): void {
    this.store.loadEntitiesByIds(params);
  }

  public clearEntities(): void {
    this.store.clearEntities();
  }

  public setEntities(entities: T[]): void {
    this.store.setEntities(entities);
  }

  // data
  public loadData(isFullRefresh = false, allowLoadMore = false, limit: number = null): void {
    this.store.loadData({ isFullRefresh, allowLoadMore, limit });
  }

  public refreshData(): void {
    this.loadData(true);
  }

  public clearData(): void {
    this.store.clearData();
  }

  // paging
  public loadPage(params: { filters?: QueryParams; pageNumber?: number; perPage?: number; isFullRefresh?: boolean }): void {
    this.store.loadPage(params);
  }

  public setPageNumber(pageNumber: number): void {
    this.store.setPageNumber(pageNumber);
  }

  public setPerPage(perPage: number): void {
    this.store.setPerPage(perPage);
  }

  public setSortSettings(sort: SortParams): void {
    this.store.setSortSettings(sort);
  }

  public refreshPaging(): void {
    this.loadPage({ isFullRefresh: true });
  }

  public clearPaging(): void {
    this.store.clearPages();
  }

  public clearAll(): void {
    this.store.clearAll();
  }

  public refreshDataAndPaging(): void {
    this.store.refreshDataAndPaging();
  }

  // ---------------------------------------------------------------------------
  // CRUD helpers
  // ---------------------------------------------------------------------------

  public refreshAfterCRUD(newEntity: T = null, nameField: string = null): void {
    const hasNewEntity = !!newEntity;
    if (hasNewEntity) {
      const oldEntity = this.store.entityById(this.store.entityId(newEntity))();
      const updateEntityOnly =
        (oldEntity as Record<string, unknown>)?.[nameField ?? 'name'] === (newEntity as Record<string, unknown>)?.[nameField ?? 'name'];
      this.setEntities([newEntity]);
      if (updateEntityOnly) return;
    }
    this.refreshInvalid(this.store.selectDataLoaded(), this.store.selectPagingLoaded(), this.pagingEmptyFiltersAndFirstCurrentPage());
  }

  public invalidate(): void {
    this.refreshInvalid(this.store.selectDataLoaded(), this.store.selectPagingLoaded(), this.pagingEmptyFiltersAndFirstCurrentPage());
  }

  private refreshInvalid(dataLoaded: boolean, pagingLoaded: boolean, initialPageAndFilter: boolean): void {
    if (pagingLoaded && dataLoaded && initialPageAndFilter) {
      return this.refreshDataAndPaging();
    }
    if (dataLoaded) this.refreshData();
    if (pagingLoaded) this.refreshPaging();
  }

  // ---------------------------------------------------------------------------
  // Direct API access (bypasses store loading flags, updates entities in store)
  // ---------------------------------------------------------------------------

  public directActions: {
    getEntityById: (id: string) => Observable<T> | null;
    getEntitiesByIds: (ids: string[]) => Observable<T[]> | null;
  } = {
    getEntityById: (id: string) => this.store.directGetEntityById?.(id) ?? null,
    getEntitiesByIds: (ids: string[]) => this.store.directGetEntitiesByIds?.(ids) ?? null
  };
}
